import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Trophy,
  TrendingDown,
} from 'lucide-react';
import { quotesApi, handleApiError } from '../lib/api';

interface RequestedItem {
  id: string;
  itemNumber: number;
  description: string;
  specifications?: string;
  quantity: number;
  unit: string;
  requiredBrand?: string;
}

interface QuotedItemInfo {
  id: string;
  brand?: string;
  model?: string;
  unitPrice: number;
  totalPrice: number;
  deliveryTime?: string;
  warranty?: string;
}

interface SupplierQuote {
  supplierId: string;
  supplierName: string;
  quoteId: string;
  quoted: boolean;
  item: QuotedItemInfo | null;
}

interface ComparisonItem {
  requestedItem: RequestedItem;
  quotedBy: SupplierQuote[];
  bestPrice?: {
    supplierId: string;
    supplierName: string;
    unitPrice: number;
  };
}

interface SupplierMetric {
  supplierId: string;
  supplierName: string;
  quoteId: string;
  quotedItems: number;
  totalItems: number;
  completionPercentage: number;
  total?: number;
  currency: string;
}

interface ComparisonData {
  quoteRequest: {
    id: string;
    code: string;
    title: string;
    status: string;
  };
  comparison: ComparisonItem[];
  supplierMetrics: SupplierMetric[];
  summary: {
    totalRequestedItems: number;
    totalQuotes: number;
    fullyQuotedSuppliers: number;
  };
}

export function QuotationCompare() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ComparisonData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchComparison();
    }
  }, [id]);

  const fetchComparison = async () => {
    try {
      setIsLoading(true);
      const response = await quotesApi.getComparison(id!);
      setData(response.data.data || null);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'COP') => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <AlertCircle className="w-12 h-12 text-red-400" />
        <p className="text-slate-400">{error || 'No se pudo cargar la comparativa'}</p>
        <button
          onClick={() => navigate(`/quotations/${id}`)}
          className="text-emerald-400 hover:text-emerald-300"
        >
          Volver a la cotizacion
        </button>
      </div>
    );
  }

  const suppliers = data.supplierMetrics;
  const lowestTotalSupplier = suppliers.reduce((min, curr) =>
    (curr.total || Infinity) < (min.total || Infinity) ? curr : min
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button
          onClick={() => navigate(`/quotations/${id}`)}
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="text-emerald-400 font-mono">{data.quoteRequest.code}</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Comparativa de Cotizaciones</h1>
          <p className="text-slate-400 mt-1">{data.quoteRequest.title}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <p className="text-sm text-slate-400 mb-1">Items Solicitados</p>
          <p className="text-2xl font-bold text-white">{data.summary.totalRequestedItems}</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <p className="text-sm text-slate-400 mb-1">Cotizaciones Recibidas</p>
          <p className="text-2xl font-bold text-white">{data.summary.totalQuotes}</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <p className="text-sm text-slate-400 mb-1">Proveedores Completos</p>
          <p className="text-2xl font-bold text-green-400">{data.summary.fullyQuotedSuppliers}</p>
        </div>
      </div>

      {/* Supplier Summary */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Resumen por Proveedor</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {suppliers.map((supplier) => {
            const isLowest = supplier.supplierId === lowestTotalSupplier.supplierId && supplier.total;
            const isComplete = supplier.completionPercentage === 100;

            return (
              <div
                key={supplier.supplierId}
                className={`rounded-lg p-4 border ${
                  isLowest
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : 'bg-slate-800/50 border-slate-700'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-medium text-white">{supplier.supplierName}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {isComplete ? (
                        <span className="flex items-center gap-1 text-xs text-green-400">
                          <CheckCircle className="w-3 h-3" />
                          Completo
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-yellow-400">
                          <AlertCircle className="w-3 h-3" />
                          {supplier.completionPercentage}%
                        </span>
                      )}
                    </div>
                  </div>
                  {isLowest && (
                    <div className="flex items-center gap-1 text-emerald-400">
                      <Trophy className="w-5 h-5" />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Items cotizados:</span>
                    <span className="text-white">
                      {supplier.quotedItems} / {supplier.totalItems}
                    </span>
                  </div>
                  {supplier.total && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Total:</span>
                      <span className={`font-bold ${isLowest ? 'text-emerald-400' : 'text-white'}`}>
                        {formatCurrency(supplier.total, supplier.currency)}
                      </span>
                    </div>
                  )}
                </div>
                {/* Progress bar */}
                <div className="mt-3">
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        isComplete ? 'bg-green-500' : 'bg-yellow-500'
                      }`}
                      style={{ width: `${supplier.completionPercentage}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Comparison Table */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <div className="p-4 border-b border-slate-800">
          <h2 className="text-lg font-semibold text-white">Comparativa por Item</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-300 sticky left-0 bg-slate-800 z-10">
                  Item
                </th>
                <th className="px-4 py-3 text-center text-sm font-medium text-slate-300">
                  Cantidad
                </th>
                {suppliers.map((supplier) => (
                  <th
                    key={supplier.supplierId}
                    className="px-4 py-3 text-center text-sm font-medium text-slate-300 min-w-[150px]"
                  >
                    {supplier.supplierName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {data.comparison.map((item) => (
                <tr key={item.requestedItem.id} className="hover:bg-slate-800/30">
                  <td className="px-4 py-3 sticky left-0 bg-slate-900 z-10">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-emerald-400 font-medium">
                          #{item.requestedItem.itemNumber}
                        </span>
                        <span className="text-sm text-white">{item.requestedItem.description}</span>
                      </div>
                      {item.requestedItem.specifications && (
                        <p className="text-xs text-slate-500 mt-1 max-w-[300px] truncate">
                          {item.requestedItem.specifications}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm text-white">
                      {item.requestedItem.quantity} {item.requestedItem.unit}
                    </span>
                  </td>
                  {item.quotedBy.map((quote) => {
                    const isBestPrice =
                      item.bestPrice && quote.supplierId === item.bestPrice.supplierId;

                    return (
                      <td key={quote.supplierId} className="px-4 py-3">
                        {quote.quoted && quote.item ? (
                          <div className="text-center">
                            <div
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded ${
                                isBestPrice
                                  ? 'bg-emerald-500/20 text-emerald-400'
                                  : 'bg-slate-800 text-white'
                              }`}
                            >
                              {isBestPrice && <TrendingDown className="w-3 h-3" />}
                              <span className="font-medium">
                                {formatCurrency(quote.item.unitPrice)}
                              </span>
                            </div>
                            {quote.item.brand && (
                              <p className="text-xs text-slate-400 mt-1">{quote.item.brand}</p>
                            )}
                            {quote.item.deliveryTime && (
                              <p className="text-xs text-slate-500 mt-0.5">
                                {quote.item.deliveryTime}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="flex justify-center">
                            <span className="flex items-center gap-1 text-xs text-red-400">
                              <XCircle className="w-3 h-3" />
                              No cotizo
                            </span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-6 text-sm text-slate-400">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-emerald-500/20 border border-emerald-500/30" />
          <span>Mejor precio</span>
        </div>
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-emerald-400" />
          <span>Total mas bajo</span>
        </div>
        <div className="flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-400" />
          <span>100% cotizado</span>
        </div>
      </div>
    </div>
  );
}
